import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/useAuth'
import { subscribeToMaintenanceMode, type MaintenanceModeState } from '../services/maintenanceModeService'

const STORAGE_PREFIX = 'timelining:maintenanceBanner:dismissed:'

const readDismissed = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

const writeDismissed = (key: string) => {
  try {
    window.sessionStorage.setItem(key, '1')
  } catch {
    // Storage unavailable: dismissal then lasts for this mount only.
  }
}

export type MaintenanceBanner = { visible: boolean; message: string; dismiss: () => void }

export const useMaintenanceBanner = (): MaintenanceBanner => {
  const { currentUser } = useAuth()
  const signedIn = Boolean(currentUser)
  const [state, setState] = useState<MaintenanceModeState>({ enabled: false, message: '' })
  const [dismissedKeys, setDismissedKeys] = useState<readonly string[]>([])

  useEffect(() => {
    if (!signedIn) return undefined
    // Signed-out state is hidden by `visible` below; an error leaves the last known state.
    return subscribeToMaintenanceMode(setState)
  }, [signedIn])

  const message = state.message.trim()
  // The message is part of the key, so a new message shows again this session.
  const key = `${STORAGE_PREFIX}${message}`
  const dismissed = dismissedKeys.includes(key) || readDismissed(key)

  const dismiss = useCallback(() => {
    writeDismissed(key)
    setDismissedKeys((keys) => (keys.includes(key) ? keys : [...keys, key]))
  }, [key])

  return { visible: signedIn && state.enabled && message.length > 0 && !dismissed, message, dismiss }
}
